import { useEffect, useRef, useState } from 'react'
import type { ConversationDetail, Message, InternalNote } from '@/components/inbox/types'

interface UseSelectedConversationParams {
  initialMessages: Message[]
  initialInternalNotes: InternalNote[]
  initialSelectedConversation: ConversationDetail | null
  selectedConversationId: number | null
}

export function useSelectedConversation({
  initialMessages,
  initialInternalNotes,
  initialSelectedConversation,
  selectedConversationId,
}: UseSelectedConversationParams) {
  const [selectedConversation, setSelectedConversation] = useState<ConversationDetail | null>(initialSelectedConversation)
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [internalNotes, setInternalNotes] = useState<InternalNote[]>(initialInternalNotes)
  const [typingUsers, setTypingUsers] = useState<
    Array<{ user_id: number; user_name: string; user_type: string; avatar_url: string | null }>
  >([])
  const [conversationPresence, setConversationPresence] = useState<
    Map<number, Array<{ id: number; name: string; avatar_url: string | null }>>
  >(new Map())

  // Force re-render every minute to update relative times
  const [, setTimeTick] = useState(0)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const typingTimeoutRef = useRef<{ [key: number]: number }>({})

  // Update relative times every minute
  useEffect(() => {
    const interval = setInterval(() => setTimeTick((t) => t + 1), 60000)
    return () => clearInterval(interval)
  }, [])

  // Sync state from props
  useEffect(() => {
    setMessages(prev => {
      const failedOptimistic = prev.filter(m => m._optimistic && m._sendFailed)
      return [...initialMessages, ...failedOptimistic]
    })
    setInternalNotes(initialInternalNotes)
    setSelectedConversation(initialSelectedConversation)
  }, [initialMessages, initialInternalNotes, initialSelectedConversation])

  // Reset UI state on conversation change
  useEffect(() => {
    setTypingUsers([])
  }, [selectedConversationId])

  // Scroll to bottom
  const prevConversationIdRef = useRef<number | null>(null)
  const prevMessageCountRef = useRef<number>(0)
  const isInitialMountRef = useRef(true)

  useEffect(() => {
    const isConversationChange = prevConversationIdRef.current !== selectedConversationId
    const currentMessageCount = messages.length + internalNotes.length
    const isNewMessage = !isConversationChange && currentMessageCount > prevMessageCountRef.current
    const isInitialMount = isInitialMountRef.current

    prevConversationIdRef.current = selectedConversationId
    prevMessageCountRef.current = currentMessageCount
    isInitialMountRef.current = false

    if (!isInitialMount && !isConversationChange && !isNewMessage) return
    if (!selectedConversationId) return

    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
    }

    if (isInitialMount) {
      setTimeout(() => requestAnimationFrame(scrollToBottom), 50)
    } else {
      requestAnimationFrame(() => requestAnimationFrame(scrollToBottom))
    }
  }, [messages, internalNotes, selectedConversationId])

  // Auto-scroll on content height change
  useEffect(() => {
    if (!selectedConversationId || !messagesContainerRef.current) return
    let timeoutId: number | null = null
    const observer = new ResizeObserver(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
    })
    observer.observe(messagesContainerRef.current)
    timeoutId = window.setTimeout(() => observer.disconnect(), 3000)
    return () => {
      observer.disconnect()
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [selectedConversationId])

  return {
    selectedConversation,
    setSelectedConversation,
    messages,
    setMessages,
    internalNotes,
    setInternalNotes,
    typingUsers,
    setTypingUsers,
    conversationPresence,
    setConversationPresence,
    messagesEndRef,
    messagesContainerRef,
    typingTimeoutRef,
  }
}
